// DGTG in six languages. English stays at its own URLs; each other language gets a prefix:
//   dgtg.in/ar/service/lead-generation   (on the preview domain: /dgtg/ar/service/lead-generation)
// Translations are source-keyed: t('Buy Leads') looks the English text up in src/i18n/dgtg/<lang>.json and
// falls back to English, so data files and components keep their English text as the single source.
// The dictionaries are generated by carbikes_metro_backend/scripts/translate_dgtg.py and loaded lazily,
// one language at a time, so English visitors download none of them.
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const LANGS = [
  { code: 'en', native: 'English', english: 'English', dir: 'ltr', hreflang: 'en' },
  { code: 'ar', native: 'العربية', english: 'Arabic', dir: 'rtl', hreflang: 'ar' },
  { code: 'zh', native: '中文', english: 'Chinese', dir: 'ltr', hreflang: 'zh-CN' },
  { code: 'pt', native: 'Português', english: 'Portuguese', dir: 'ltr', hreflang: 'pt-BR' },
  { code: 'es', native: 'Español', english: 'Spanish', dir: 'ltr', hreflang: 'es' },
  { code: 'fr', native: 'Français', english: 'French', dir: 'ltr', hreflang: 'fr' },
];
export const LANG_CODES = LANGS.filter((l) => l.code !== 'en').map((l) => l.code);
export const LANG_BY_CODE = Object.fromEntries(LANGS.map((l) => [l.code, l]));
export const ORIGIN = 'https://www.dgtg.in';

// Pages that exist in every language. Everything else (blog, legal, world data, calculator) is English only.
const TRANSLATABLE = /^(\/|\/about|\/contact|\/partners|\/events|\/service\/[\w-]+|\/industry\/[\w-]+|\/campaigns(\/[\w-]+)?|\/grow(\/[\w-]+)?)$/;

// '/dgtg/ar/service/x' or '/ar/service/x' -> { prefix: '/dgtg' | '', lang: 'ar', rest: '/service/x' }
export function splitPath(pathname) {
  let p = pathname || '/';
  let prefix = '';
  if (p === '/dgtg' || p.startsWith('/dgtg/')) { prefix = '/dgtg'; p = p.slice(5) || '/'; }
  const m = p.match(/^\/([a-z]{2})(\/.*)?$/);
  if (m && LANG_CODES.includes(m[1])) return { prefix, lang: m[1], rest: m[2] || '/' };
  return { prefix, lang: 'en', rest: p };
}

export function pathFor(lang, rest, prefix = '') {
  const r = rest === '/' ? '' : rest;
  const out = `${prefix}${lang === 'en' ? '' : `/${lang}`}${r}`;
  return out || '/';
}

export const isTranslatable = (rest) => TRANSLATABLE.test(rest || '/');

const LangContext = createContext({ lang: 'en', dir: 'ltr', t: (s) => s });
export const useLang = () => useContext(LangContext);
export const useT = () => useContext(LangContext).t;

// Absolute canonical URL for a site path in the current language: canon('/about') -> https://www.dgtg.in/ar/about
export function useCanonical() {
  const { lang } = useLang();
  return (rest) => {
    const p = pathFor(lang, rest || '/', '');
    return p === '/' ? ORIGIN : `${ORIGIN}${p}`;
  };
}

// A dgtg link in the current language: '/dgtg/service/x' -> '/dgtg/ar/service/x' when the page is Arabic and
// the target exists in Arabic. Used by the site-aware Link, so page code keeps writing English paths.
export function localizePath(to, lang) {
  if (!lang || lang === 'en' || typeof to !== 'string' || !(to === '/dgtg' || to.startsWith('/dgtg/') || to.startsWith('/dgtg#'))) return to;
  const hash = to.includes('#') ? to.slice(to.indexOf('#')) : '';
  const base = hash ? to.slice(0, to.indexOf('#')) : to;
  const rest = base.slice(5) || '/';
  return isTranslatable(rest) ? `${pathFor(lang, rest, '/dgtg')}${hash}` : to;
}

const cache = {};
export function DGTGLangScope({ lang, children }) {
  const [dict, setDict] = useState(cache[lang] || null);
  useEffect(() => {
    let alive = true;
    if (!cache[lang]) {
      import(`./dgtg/${lang}.json`).then((m) => { cache[lang] = m.default || m; if (alive) setDict(cache[lang]); })
        .catch(() => { cache[lang] = {}; if (alive) setDict({}); });
    } else setDict(cache[lang]);
    return () => { alive = false; };
  }, [lang]);
  const info = LANG_BY_CODE[lang] || LANGS[0];
  useEffect(() => {
    const h = document.documentElement;
    const prev = { lang: h.lang, dir: h.dir };
    h.lang = info.hreflang; h.dir = info.dir;
    return () => { h.lang = prev.lang || 'en'; h.dir = prev.dir || 'ltr'; };
  }, [info]);
  if (!dict) return null;
  const t = (s) => (typeof s === 'string' && dict[s]) || s;
  return <LangContext.Provider value={{ lang, dir: info.dir, t }}>{children}</LangContext.Provider>;
}

// Reads the language from the URL for components rendered outside a scope (nav, alternates, suggestion bar).
export function useUrlLang() {
  const { pathname } = useLocation();
  return splitPath(pathname);
}

export const PREF_KEY = 'dgtg_lang';
export function readPref() { try { return localStorage.getItem(PREF_KEY); } catch { return null; } }
export function savePref(code) { try { localStorage.setItem(PREF_KEY, code); } catch { /* ignore */ } }
